// English strings for visitor-portal receipts, the optional UAE PASS signing step and device badge
// printing at the kiosk and the reception desk (portal stream: PB-05-04, PB-08-13, PB-05-12,
// PB-06-09; PD-510 – PD-515). Arabic: ar-portal.ts.
export const enPortal = {
  // API messages
  'portal.v.printerUnknown': 'Choose an allowed edge printer at this kiosk’s site.',
  'portal.api.printRetryLimit': 'The kiosk has already tried to print this badge twice. Please collect your badge at reception.',
  'portal.api.printNotRetryable': 'This badge cannot be printed again here. Please ask at reception.',
  'portal.api.printerUnavailable': 'The badge printer is not available. Please collect your badge at reception.',

  // Notice receipt (PB-05-04, PD-510)
  'portal.receipt.title': 'Your receipt',
  'portal.receipt.notice': 'Notice',
  'portal.receipt.version': '{title}, version {number}',
  'portal.receipt.decision': 'Your decision',
  'portal.receipt.decision.accepted': 'Accepted',
  'portal.receipt.decision.signed': 'Signed with your typed name',
  'portal.receipt.decision.declined': 'Declined',
  'portal.receipt.signedName': 'Name typed as signature',
  'portal.receipt.time': 'Recorded',
  'portal.receipt.where': 'Where',
  'portal.receipt.channel.portal': 'Visitor pass',
  'portal.receipt.channel.kiosk': 'Self-service kiosk',
  'portal.receipt.channel.reception': 'Reception',
  'portal.receipt.digest': 'Receipt code',
  'portal.receipt.digestShow': 'Show the full code',
  'portal.receipt.digestHint': 'This code is a SHA-256 fingerprint of the notice text you saw, your decision, the name you typed and the time. Keep it if you want to refer to this decision later.',
  'portal.receipt.legal': 'This receipt records your decision. It is not a statement about the legal effect of the notice.',
  'portal.receipt.declinedNote': 'A decline is recorded as a decline. It never counts as agreement.',
  'portal.receipt.notCurrent': 'A newer version of this notice has been published since this decision.',
  'portal.receipt.uaePassSigned': 'Also signed with UAE PASS on {when}.',
  'portal.receipt.uaePassArtifact': 'Signed document code',

  // Optional UAE PASS signing (PB-08-13, PD-511)
  'portal.sign.title': 'Sign with UAE PASS (optional)',
  'portal.sign.intro': 'You can also sign this record with UAE PASS. Your typed signature is already recorded; this step is optional and does not change your visit.',
  'portal.sign.scope': 'Permission asked of UAE PASS',
  'portal.sign.document': 'Code of the record being signed',
  'portal.sign.consent': 'I agree to be sent to UAE PASS to sign this record. Passage receives the signing result, never my UAE PASS password.',
  'portal.sign.start': 'Sign with UAE PASS',
  'portal.sign.starting': 'Opening UAE PASS…',
  'portal.sign.errorHint': 'Your typed signature is still recorded. Nothing else is needed for your visit.',
  'portal.sign.providerUnavailable': 'UAE PASS signing is not available right now. Your typed signature is recorded and nothing else is needed.',
  'portal.sign.last.started': 'A UAE PASS signing request is open. If you closed UAE PASS, you can start again.',
  'portal.sign.last.cancelled': 'You declined signing in UAE PASS. Your typed signature stays recorded; you can try again.',
  'portal.sign.last.failed': 'UAE PASS signing did not complete. Your typed signature stays recorded; you can try again.',
  'portal.sign.last.unknown': 'UAE PASS did not answer in time, so the record is not shown as signed. You can try again.',
  'portal.sign.last.expired': 'The UAE PASS signing request expired. You can start again.',

  // Return page after UAE PASS signing
  'portal.ret.signTitle': 'UAE PASS signing',
  'portal.ret.sign.signed': 'Signed with UAE PASS. The signature is shown on the receipt in your visitor pass.',
  'portal.ret.sign.cancelled': 'You declined signing in UAE PASS. Nothing changed: your typed signature stays recorded.',
  'portal.ret.sign.failed': 'UAE PASS signing did not complete. Your typed signature stays recorded; you can try again from your visitor pass.',
  'portal.ret.sign.unknown': 'UAE PASS did not answer in time, so the record is not shown as signed. You can try again from your visitor pass.',

  // Kiosk badge printing (PB-05-12, PD-514, PD-515)
  'portal.kiosk.badge.printing': 'Printing your badge…',
  'portal.kiosk.badge.printed': 'Your badge has been printed. Please take it from the printer.',
  'portal.kiosk.badge.failed': 'Your badge did not print. You are checked in. You can try again, or collect your badge at reception.',
  'portal.kiosk.badge.unknown': 'We could not confirm whether your badge printed. You are checked in. Please look at the printer or ask at reception; reception has been told.',
  'portal.kiosk.badge.unavailable': 'You are checked in. Please collect your badge at reception.',
  'portal.kiosk.badge.retry': 'Try printing again',
  'portal.kiosk.badge.limit': 'Please collect your badge at reception.',

  // Kiosk administration
  'portal.kiosks.badgePrint': 'Print the badge on this kiosk’s printer',
  'portal.kiosks.printer': 'Kiosk badge printer',
  'portal.kiosks.printerHint': 'Allowed edge printers at this site (Integrations → Devices). If the printer cannot be reached, visitors are sent to reception and reception is told.',
  'portal.kiosks.printerNone': 'No allowed printer at this site yet. Allow one under Integrations → Devices first.',
  'portal.kiosks.printerChoose': 'Choose a printer',

  // Printers (shared)
  'portal.printer.label': '{agent} · {printer}',
  'portal.printer.simulator': 'simulator',
  'portal.printer.offline': 'agent not active',
  'portal.printer.kiosk': 'used by a kiosk',

  // Reception desk printer (PB-06-09, PD-513)
  'portal.desk.printer': 'Print on',
  'portal.desk.browser': 'This computer (browser print)',
  'portal.desk.device': 'Desk printer: {name}',
  'portal.desk.status': 'Desk printer job',
  'portal.desk.status.queued': 'Queued',
  'portal.desk.status.sent': 'Sent to the printer, waiting for its answer',
  'portal.desk.status.printed': 'Printed',
  'portal.desk.status.failed': 'Failed: nothing printed. Print again with a reason, or use the browser print.',
  'portal.desk.status.unknown': 'Unknown: the printer did not confirm. Check the printer before printing again; a reception task was opened.',
  'portal.desk.status.cancelled': 'Cancelled',
  'portal.desk.preview': 'Badge sent to the desk printer',
  'portal.desk.noCredential': 'Printing a badge never issues or changes an access credential.',
} as const;
